import CompanyTableCell from "../../CompanyTableCell";
import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

import TabSectionHeader from "../../TabSectionHeader";
import CompanyProfileDrawer from "../../CompanyProfileDrawer";
import { TAB_LIGHT_BLUE } from "../CompanyTabHeaderShared";
import { ReactComponent as LongLeftArrow } from "../../../assets/icons/long-left-arrow.svg";
import { ReactComponent as LongRightArrow } from "../../../assets/icons/long-right-arrow.svg";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const PAGE_SIZE = 8;

const STATUS_STYLES = {
  Listed: {
    backgroundColor: "#E8FFF4",
    color: "#22C55E",
  },
  "Not Listed": {
    backgroundColor: "#ECEFF4",
    color: "#6B7280",
  },
};

const COMPANY_AVATAR_BACKGROUNDS = [
  "linear-gradient(135deg, #E0EAFF 0%, #B8CCFF 100%)",
  "linear-gradient(135deg, #E9F7FF 0%, #C8E7FF 100%)",
  "linear-gradient(135deg, #FFF0DF 0%, #FFD0A8 100%)",
  "linear-gradient(135deg, #E4F1FF 0%, #B9D9FF 100%)",
  "linear-gradient(135deg, #E7FFE9 0%, #BFF3C8 100%)",
  "linear-gradient(135deg, #F0E9FF 0%, #D8C6FF 100%)",
];

const columns = [
  { id: "company", label: "Company Name", align: "left" },
  { id: "status", label: "Status", align: "left" },
  { id: "jobs", label: "Jobs", align: "center" },
  { id: "candidates", label: "Candidates", align: "left" },
  { id: "companyCandidates", label: "Company Candidates", align: "left" },
  { id: "industry", label: "Industry", align: "left" },
  { id: "actions", label: "", align: "right", sortable: false },
];

const headerCellSx = {
  backgroundColor: "#F8FAFC",
  borderBottom: "1px solid #EAECF0",
  color: "#667085",
  fontSize: 11,
  fontWeight: 500,
  fontFamily: "Be Vietnam Pro",
  py: 1.35,
  px: 2,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  borderBottom: "1px solid #E5EAF1",
  color: "#344054",
  fontSize: 13,
  fontFamily: "Be Vietnam Pro",
  py: 1.7,
  px: 2,
  verticalAlign: "middle",
};

function createPaginationItems(currentPageIndex, pageCount) {
  const current = currentPageIndex + 1;
  const pages = [];

  if (pageCount <= 7) {
    for (let index = 1; index <= pageCount; index += 1) pages.push(index);
    return pages;
  }

  if (current <= 4) {
    pages.push(1, 2, 3, 4, "...", pageCount - 1, pageCount);
    return pages;
  }

  if (current >= pageCount - 3) {
    pages.push(1, 2, "...", pageCount - 3, pageCount - 2, pageCount - 1, pageCount);
    return pages;
  }

  pages.push(1, "...", current - 1, current, current + 1, "...", pageCount);
  return pages;
}

function cleanText(value) {
  return String(value ?? "").trim();
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
}

function formatWebsiteLabel(value) {
  const normalized = cleanText(value);
  if (!normalized) return "";
  return normalized
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/, "");
}

function normalizeAvatarItems(value) {
  if (Array.isArray(value)) {
    return value.map((item) => cleanText(item)).filter(Boolean);
  }

  const normalized = cleanText(value);
  if (!normalized) return [];
  return normalized
    .split(",")
    .map((item) => cleanText(item))
    .filter(Boolean);
}

function normalizeCompetitorRow(row = {}, index = 0) {
  const jobsValue = Number(row.jobs ?? row.job_openings);
  const hasJobs = Number.isFinite(jobsValue) && jobsValue > 0;
  const rawStatus = cleanText(row.status);
  const status =
    rawStatus === "Listed" || rawStatus === "Not Listed"
      ? rawStatus
      : hasJobs
      ? "Listed"
      : "Not Listed";

  const candidateAvatars = normalizeAvatarItems(row.candidatesAvatars ?? row.candidate_avatars).slice(0, 3);
  const totalCandidatesRaw = Number(
    row.totalCandidates ?? row.total_candidates ?? row.candidate_count ?? row.total_candidates_count
  );
  const candidatesExtraRaw = Number(row.candidatesExtra ?? row.candidates_extra ?? 0);
  const totalCandidates = Number.isFinite(totalCandidatesRaw)
    ? totalCandidatesRaw
    : candidateAvatars.length + (Number.isFinite(candidatesExtraRaw) ? candidatesExtraRaw : 0);
  const candidatesExtra = Math.max(0, totalCandidates - candidateAvatars.length);

  const companyCandidateAvatars = normalizeAvatarItems(
    row.companyCandidatesAvatars ?? row.company_candidate_avatars
  ).slice(0, 3);
  const totalCompanyCandidatesRaw = Number(
    row.totalCompanyCandidates ??
      row.total_company_candidates ??
      row.company_candidate_count ??
      row.total_company_candidates_count
  );
  const companyCandidatesExtraRaw = Number(
    row.companyCandidatesExtra ?? row.company_candidates_extra ?? 0
  );
  const totalCompanyCandidates = Number.isFinite(totalCompanyCandidatesRaw)
    ? totalCompanyCandidatesRaw
    : companyCandidateAvatars.length +
      (Number.isFinite(companyCandidatesExtraRaw) ? companyCandidatesExtraRaw : 0);
  const companyCandidatesExtra = Math.max(
    0,
    totalCompanyCandidates - companyCandidateAvatars.length
  );

  return {
    id: Number(row.id) || index + 1,
    name: cleanText(row.name || row.company_name) || `Competitor ${index + 1}`,
    website: formatWebsiteLabel(row.website || row.company_website || row.website_url),
    logo: cleanText(row.logo || row.company_logo || row.logo_url),
    status,
    jobs: hasJobs ? jobsValue : null,
    jobsSort: hasJobs ? jobsValue : -1,
    candidatesAvatars: candidateAvatars,
    totalCandidates,
    candidatesExtra,
    companyCandidatesAvatars: companyCandidateAvatars,
    totalCompanyCandidates,
    companyCandidatesExtra,
    industry: cleanText(row.industry) || "Technology",
  };
}

function compareValues(left, right, order = "asc") {
  if (left < right) return order === "asc" ? -1 : 1;
  if (left > right) return order === "asc" ? 1 : -1;
  return 0;
}

function sortCompetitorRows(rows = [], orderBy = "company", order = "asc") {
  const accessorMap = {
    company: (row) => cleanText(row.name).toLowerCase(),
    status: (row) => (row.status === "Listed" ? 1 : 0),
    jobs: (row) => row.jobsSort,
    candidates: (row) => Number(row.totalCandidates || 0),
    companyCandidates: (row) => Number(row.totalCompanyCandidates || 0),
    industry: (row) => cleanText(row.industry).toLowerCase(),
  };

  const accessor = accessorMap[orderBy] || accessorMap.company;
  return [...rows].sort((left, right) => {
    const result = compareValues(accessor(left), accessor(right), order);
    if (result !== 0) return result;
    return cleanText(left.name).localeCompare(cleanText(right.name));
  });
}

function AvatarStack({ avatars = [], extra = 0 }) {
  if (!avatars.length && !extra) {
    return (
      <Typography
        sx={{
          color: "#98A2B3",
          fontSize: 14,
          fontFamily: "Be Vietnam Pro",
        }}
      >
        -
      </Typography>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0}>
      {avatars.map((avatar, index) => (
        <Avatar
          key={`${avatar}-${index}`}
          src={avatar || undefined}
          sx={{
            width: 28,
            height: 28,
            ml: index === 0 ? 0 : -0.85,
            border: "2px solid #FFFFFF",
            boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.06)",
            background: COMPANY_AVATAR_BACKGROUNDS[index % COMPANY_AVATAR_BACKGROUNDS.length],
            fontSize: 11,
            fontWeight: 700,
            color: "#3451F4",
          }}
        />
      ))}

      {extra > 0 ? (
        <Box
          sx={{
            ml: 0.9,
            minWidth: 28,
            height: 22,
            px: 0.85,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "999px",
            backgroundColor: "#F4EBFF",
          }}
        >
          <Typography
            sx={{
              color: "#9C4FFF",
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1,
              fontFamily: "Be Vietnam Pro",
            }}
          >
            +{extra}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}

function StatusChip({ status = "Not Listed" }) {
  const resolvedStatus = status === "Listed" ? "Listed" : "Not Listed";
  const styles = STATUS_STYLES[resolvedStatus];

  return (
    <Chip
      label={resolvedStatus}
      data-table-status
      size="small"
      sx={{
        height: 24,
        borderRadius: "999px",
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "Be Vietnam Pro",
        "& .MuiChip-label": {
          px: 1.25,
        },
      }}
    />
  );
}

export default function CompanyCompetitorsSection({
  leadId = null,
  companyId = null,
  entityType = "lead",
  companyProfileEnrichmentPending = false,
}) {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderBy, setOrderBy] = useState("company");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [competitorDrawerOpen, setCompetitorDrawerOpen] = useState(false);
  const [scanningCompetitors, setScanningCompetitors] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const normalizedEntityType = String(entityType || "lead").toLowerCase();
  const resolvedLeadId = Number(leadId || 0);
  const resolvedCompanyId = Number(companyId || 0);

  const endpoint = useMemo(() => {
    if (normalizedEntityType === "company" && resolvedCompanyId > 0) {
      return `${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyId))}/competitors`;
    }
    if (resolvedLeadId > 0) {
      return `${API_BASE}/api/leads/${encodeURIComponent(String(resolvedLeadId))}/competitors`;
    }
    if (resolvedCompanyId > 0) {
      return `${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyId))}/competitors`;
    }
    return "";
  }, [normalizedEntityType, resolvedCompanyId, resolvedLeadId]);

  useEffect(() => {
    if (!endpoint) {
      setCompetitors([]);
      setLoading(false);
      setError("");
      return;
    }

    let active = true;
    const controller = new AbortController();

    setLoading(true);
    setError("");

    fetch(endpoint, {
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const message = await response.text().catch(() => "");
          throw new Error(message || "Failed to load competitors");
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const rows = Array.isArray(payload?.competitors)
          ? payload.competitors
          : Array.isArray(payload?.rows)
          ? payload.rows
          : [];
        setCompetitors(rows.map((row, index) => normalizeCompetitorRow(row, index)));
        setPage(0);
      })
      .catch((fetchError) => {
        if (!active || fetchError?.name === "AbortError") return;
        console.error("Failed to load competitors:", fetchError);
        setError(fetchError?.message || "Could not load competitors.");
        setCompetitors([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [endpoint, companyProfileEnrichmentPending, refreshKey]);

  const sortedRows = useMemo(
    () => sortCompetitorRows(competitors, orderBy, order),
    [competitors, orderBy, order]
  );
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

  useEffect(() => {
    setPage((previous) => Math.min(previous, pageCount - 1));
  }, [pageCount]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [page, sortedRows]
  );
  const paginationItems = useMemo(
    () => createPaginationItems(page, pageCount),
    [page, pageCount]
  );

  const handleGetCompetitors = async () => {
    if (!Number.isFinite(resolvedCompanyId) || resolvedCompanyId <= 0 || scanningCompetitors) {
      return;
    }

    setScanningCompetitors(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyId))}/competitors/scan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          credentials: "include",
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to get company competitors.");
      }

      setRefreshKey((previous) => previous + 1);
    } catch (scanError) {
      console.error("Failed to scan company competitors:", scanError);
      setError(scanError?.message || "Failed to get company competitors.");
    } finally {
      setScanningCompetitors(false);
    }
  };

  const handleSort = (columnId) => {
    if (!columnId || columnId === "actions") return;
    if (orderBy === columnId) {
      setOrder((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(columnId);
    setOrder("asc");
  };

  const handleOpenCompetitor = (row) => {
    if (!row?.id) return;

    setSelectedCompetitor({
      id: row.id,
      lead_id: row.id,
      name: row.name,
      company_name: row.name,
      logo: row.logo,
      company_logo: row.logo,
      website: row.website,
      industry: row.industry,
      status: row.status,
    });
    setCompetitorDrawerOpen(true);
  };

  const handleCloseCompetitorDrawer = () => {
    setCompetitorDrawerOpen(false);
  };

  const renderSortHeader = (column) => {
    if (column.sortable === false || column.id === "actions") {
      return column.label;
    }

    const isActive = orderBy === column.id;
    const arrowColor = isActive ? "#475467" : "#98A2B3";

    return (
      <Box
        component="button"
        type="button"
        onClick={() => handleSort(column.id)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.2,
          p: 0,
          m: 0,
          border: 0,
          backgroundColor: "transparent",
          color: "inherit",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <Box component="span">{column.label}</Box>
        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: 16,
            color: arrowColor,
            transform: isActive && order === "desc" ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s ease",
          }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <TabSectionHeader
        title="Competitors"
        dividerColor={TAB_LIGHT_BLUE}
        actionNode={
          <Button
            variant="contained"
            size="small"
            onClick={handleGetCompetitors}
            disabled={
              scanningCompetitors ||
              !Number.isFinite(resolvedCompanyId) ||
              resolvedCompanyId <= 0
            }
            sx={{
              flexShrink: 0,
              minHeight: 32,
              px: 1.75,
              borderRadius: "999px",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: "#9C4FFF",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#8338D6", boxShadow: "none" },
            }}
          >
            {scanningCompetitors ? "Matching competitors..." : "Match Internal Competitors"}
          </Button>
        }
      />

      {error ? (
        <Typography
          sx={{
            mb: 2,
            color: "#B42318",
            fontSize: 12.5,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {error}
        </Typography>
      ) : null}

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1040, backgroundColor: "#FFFFFF" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={headerCellSx}
                >
                  {renderSortHeader(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>


          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ ...bodyCellSx, py: 5 }}>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedRows.length ? (
              paginatedRows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => handleOpenCompetitor(row)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#FCFCFD",
                      },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, minWidth: 220 }}><CompanyTableCell row={row} companyEntity onClick={(event) => { event.stopPropagation(); handleOpenCompetitor(row); }} /></TableCell>

                    <TableCell sx={{ ...bodyCellSx, minWidth: 120 }}>
                      <StatusChip status={row.status} />
                    </TableCell>

                    <TableCell align="center" sx={{ ...bodyCellSx, minWidth: 70 }}>
                      <Typography
                        sx={{
                          color: "#344054",
                          fontSize: 14,
                          fontFamily: "Be Vietnam Pro",
                        }}
                      >
                        {row.jobs != null ? row.jobs : "-"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ ...bodyCellSx, minWidth: 150 }}>
                      <AvatarStack
                        avatars={row.candidatesAvatars}
                        extra={row.candidatesExtra}
                      />
                    </TableCell>

                    <TableCell sx={{ ...bodyCellSx, minWidth: 180 }}>
                      <AvatarStack
                        avatars={row.companyCandidatesAvatars}
                        extra={row.companyCandidatesExtra}
                      />
                    </TableCell>

                    <TableCell sx={{ ...bodyCellSx, minWidth: 110 }}>
                      <Typography sx={{ color: "#475467", fontSize: 14 }}>
                        {row.industry || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={{ ...bodyCellSx, minWidth: 48 }}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        sx={{
                          color: "#667085",
                          p: 0.5,
                        }}
                      >
                        <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ ...bodyCellSx, py: 4 }}>
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: "#667085",
                      fontSize: 14,
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {endpoint
                      ? companyProfileEnrichmentPending
                        ? "AI is matching this company with competitors already saved in RPO."
                        : "No internal competitors are linked to this company yet."
                      : "Save the company first to manage competitors."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && sortedRows.length ? (
        <Box
          sx={{
            mt: 3.2,
            display: "grid",
            // Equal outer tracks keep the numbered controls centered even
            // when the previous/next labels change state or the viewport is
            // resized.
            gridTemplateColumns: "minmax(86px, 1fr) auto minmax(86px, 1fr)",
            alignItems: "center",
            columnGap: { xs: 1, sm: 2 },
            width: "100%",
            minWidth: 0,
          }}
        >
          <Button
            onClick={() => setPage((previous) => Math.max(0, previous - 1))}
            disabled={page === 0}
            sx={{
              justifySelf: "start",
              minWidth: 86,
              height: 32,
              px: 1.8,
              borderRadius: "999px",
              border: "1px solid #D0D5DD",
              color: "#98A2B3",
              textTransform: "none",
              fontSize: 11,
              fontWeight: 500,
              fontFamily: "Be Vietnam Pro",
              "& .MuiButton-startIcon": {
                mr: 0.6,
              },
              "&.Mui-disabled": {
                borderColor: "#D0D5DD",
                color: "#98A2B3",
              },
            }}
            startIcon={<LongLeftArrow width={16} height={16} />}
          >
            Previous
          </Button>

          {pageCount > 1 ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.7 }}>
              {paginationItems.map((item, index) =>
                typeof item === "number" ? (
                  <Button
                    key={item}
                    onClick={() => setPage(item - 1)}
                    sx={{
                      minWidth: 32,
                      width: 32,
                      height: 32,
                      flex: "0 0 32px",
                      p: 0,
                      borderRadius: "999px",
                      border:
                        page === item - 1 ? "1px solid #9C4FFF" : "1px solid transparent",
                      backgroundColor: page === item - 1 ? "#F9F5FF" : "transparent",
                      color: page === item - 1 ? "#9C4FFF" : "#475467",
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "Be Vietnam Pro",
                      textTransform: "none",
                    }}
                  >
                    {item}
                  </Button>
                ) : (
                  <Typography
                    key={`${item}-${index}`}
                    sx={{
                      width: 32,
                      minWidth: 32,
                      flex: "0 0 32px",
                      textAlign: "center",
                      color: "#98A2B3",
                      fontSize: 12,
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {item}
                  </Typography>
                )
              )}
            </Box>
          ) : (
            <Box sx={{ width: 32 }} />
          )}

          <Button
            onClick={() => setPage((previous) => Math.min(pageCount - 1, previous + 1))}
            disabled={page >= pageCount - 1}
            sx={{
              justifySelf: "end",
              minWidth: 86,
              height: 32,
              px: 1.8,
              borderRadius: "999px",
              backgroundColor: "#9C4FFF",
              color: "#FFFFFF",
              textTransform: "none",
              fontSize: 11,
              fontWeight: 500,
              fontFamily: "Be Vietnam Pro",
              "& .MuiButton-endIcon": {
                ml: 0.6,
              },
              "&:hover": {
                backgroundColor: "#8B3BEF",
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                border: "1px solid #D0D5DD",
                color: "#98A2B3",
              },
            }}
            endIcon={<LongRightArrow width={16} height={16} />}
          >
            Next
          </Button>
        </Box>
      ) : null}

      <CompanyProfileDrawer
        open={competitorDrawerOpen}
        onClose={handleCloseCompetitorDrawer}
        company={selectedCompetitor}
        entityType="lead"
        allowShowMore={true}
      />
    </Box>
  );
}
